/* eslint-disable @box/blueprint/html-to-blueprint-mapping */
import type { Editor } from '@tiptap/react';
import Button from 'box-ui-elements/es/components/button';
import DropdownMenu, {
  MenuToggle,
} from 'box-ui-elements/es/components/dropdown-menu';
import { Menu, MenuItem } from 'box-ui-elements/es/components/menu';
import PlainButton from 'box-ui-elements/es/components/plain-button';
import classnames from 'classnames';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import AlignCenterIcon from '../../../../../assets/icons/rich-text-editor-icons/align-center';
import AlignLeftIcon from '../../../../../assets/icons/rich-text-editor-icons/align-left';
import AlignRightIcon from '../../../../../assets/icons/rich-text-editor-icons/align-right';
import BoldIcon from '../../../../../assets/icons/rich-text-editor-icons/bold';
import CheckIcon from '../../../../../assets/icons/rich-text-editor-icons/check';
import ItalicIcon from '../../../../../assets/icons/rich-text-editor-icons/Italic';
import ListBulletIcon from '../../../../../assets/icons/rich-text-editor-icons/list-bullet';
import ListNumberIcon from '../../../../../assets/icons/rich-text-editor-icons/list-number';
import StrikeThroughIcon from '../../../../../assets/icons/rich-text-editor-icons/strike-through';
import UnderlineIcon from '../../../../../assets/icons/rich-text-editor-icons/underline';
import { FONT_SIZES, FONT_STYLES } from '../../constants';
import messages from '../../messages';
import { FontColorHighlightFlyout } from './components/font-color-highlight-flyout';
import SetLinkFlyout from './components/set-link-flyout';
import '../../styles.scss';

type Props = {
  editor: Editor;
};

const editorClassNameCallBack =
  (
    name:
      | string
      | {
          textAlign: string;
        },
  ) =>
  (editor: Editor) => (editor.isActive(name) ? 'is-active' : '');

const buttonConfig = {
  [FONT_STYLES.BOLD]: {
    onClick: (editor: Editor) => editor.chain().focus().toggleBold().run(),
    getClassName: editorClassNameCallBack('bold'),
    icon: <BoldIcon />,
  },
  [FONT_STYLES.ITALIC]: {
    onClick: (editor: Editor) => editor.chain().focus().toggleItalic().run(),
    getClassName: editorClassNameCallBack('italic'),
    icon: <ItalicIcon />,
  },
  [FONT_STYLES.UNDERLINE]: {
    onClick: (editor: Editor) => editor.chain().focus().toggleUnderline().run(),
    getClassName: editorClassNameCallBack('underline'),
    icon: <UnderlineIcon />,
  },
  [FONT_STYLES.STRIKETHROUGH]: {
    onClick: (editor: Editor) => editor.chain().focus().toggleStrike().run(),
    getClassName: editorClassNameCallBack('strike'),
    icon: <StrikeThroughIcon />,
  },
} as const;

export const BuieTextEditorToolbar = ({ editor }: Props) => {
  const setLink = useCallback(
    (url: string) => {
      // empty
      if (url === '') {
        return;
      }

      // update link
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    },
    [editor],
  );

  const delLink = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
  }, [editor]);

  const setTextColor = useCallback(
    (color: string) => {
      editor.chain().focus().setColor(color).run();
    },
    [editor],
  );

  const setTextHighlight = useCallback(
    (color: string) => {
      editor.chain().focus().toggleHighlight({ color }).run();
    },
    [editor],
  );

  const isColorActive = useCallback(
    (color: string) => {
      return editor.isActive('textStyle', { color });
    },
    [editor],
  );

  const isHighlightActive = useCallback(
    (color: string) => {
      return editor.isActive('highlight', { color });
    },
    [editor],
  );

  if (!editor) return null;

  const { fontSize: activeFontSize } = editor?.getAttributes('textStyle') || {};
  const activeFontSizeNum =
    parseInt(activeFontSize?.replace(/\D/g, ''), 10) || 12;

  const fontSizeDropDown = (
    <DropdownMenu className="fontsize-menu-dropdown" isResponsive>
      <PlainButton className="fontsize-dropdown-button" type="button">
        <MenuToggle>
          <FormattedMessage
            {...messages.fontSizeName}
            values={{ num: activeFontSizeNum }}
          />
        </MenuToggle>
      </PlainButton>
      <Menu>
        {FONT_SIZES.map(num => {
          const fontSize = `${num}pt`;
          const isFontSizeActive = editor.isActive('textStyle', {
            fontSize,
          });
          return (
            <MenuItem
              key={`fontsize-${num}`}
              className={isFontSizeActive ? 'is-active' : ''}
              onClick={() =>
                editor?.chain().focus().setFontSize(fontSize).run()
              }
            >
              <span>{isFontSizeActive && <CheckIcon />}</span>
              <span>
                <FormattedMessage {...messages.fontSizeName} values={{ num }} />
              </span>
            </MenuItem>
          );
        })}
      </Menu>
    </DropdownMenu>
  );

  const paragraphOnClick = (name: string) => () =>
    editor.chain().focus().setTextAlign(name).run();

  const paragraphStylesDropdown = (
    <DropdownMenu isResponsive>
      <PlainButton
        className="paragraph-dropdown-button"
        data-testid="paragraph-menu-button"
        type="button"
      >
        <MenuToggle>
          <AlignLeftIcon height={20} width={20} />
        </MenuToggle>
      </PlainButton>
      <Menu className="paragraph-dropdown-menu">
        <div data-testid="paragraph-style-dropdown">
          {editor &&
            [
              {
                name: 'left',
                icon: <AlignLeftIcon />,
              },
              {
                name: 'center',
                icon: <AlignCenterIcon />,
              },
              {
                name: 'right',
                icon: <AlignRightIcon />,
              },
            ].map(({ name, icon }) => (
              <Button
                key={name}
                className={classnames(
                  'style-icon-button',
                  editorClassNameCallBack({ textAlign: name })(editor),
                )}
                icon={icon}
                onClick={paragraphOnClick(name)}
              />
            ))}
        </div>
      </Menu>
    </DropdownMenu>
  );

  const listStylesDropdown = (
    <DropdownMenu isResponsive>
      <PlainButton
        className="liststyle-dropdown-button"
        data-testid="liststyle-menu-button"
        type="button"
      >
        <MenuToggle>
          <ListBulletIcon height={20} width={20} />
        </MenuToggle>
      </PlainButton>
      <Menu className="liststype-dropdown-menu">
        <div data-testid="liststyle-dropdown">
          {editor &&
            [
              {
                name: 'bulletList',
                icon: <ListBulletIcon />,
                click: () => editor.chain().focus().toggleBulletList().run(),
              },
              {
                name: 'orderedList',
                icon: <ListNumberIcon />,
                click: () => editor.chain().focus().toggleOrderedList().run(),
              },
            ].map(({ name, icon, click }) => (
              <Button
                key={name}
                className={classnames(
                  'style-icon-button',
                  editorClassNameCallBack(name)(editor),
                )}
                icon={icon}
                onClick={click}
                type="button"
              />
            ))}
        </div>
      </Menu>
    </DropdownMenu>
  );

  return (
    <>
      {fontSizeDropDown}
      <FontColorHighlightFlyout
        handleSetColor={setTextColor}
        handleSetHighlight={setTextHighlight}
        isColorActive={isColorActive}
        isHighlightActive={isHighlightActive}
      />
      <div className="vl" />
      {Object.entries(buttonConfig).map(
        ([key, buttonProps]) =>
          editor && (
            <Button
              key={key}
              className={classnames(
                'style-icon-button',
                buttonProps.getClassName(editor),
              )}
              data-testid={`${key}-icon-button`}
              icon={buttonProps.icon}
              onClick={() => buttonProps.onClick(editor)}
              type="button"
            />
          ),
      )}
      <div className="vl" />
      {listStylesDropdown}
      {paragraphStylesDropdown}
      <div className="vl" />
      <SetLinkFlyout
        delLink={delLink}
        isActive={editor?.isActive('link')}
        previousUrl={editor?.getAttributes('link')?.href}
        setLink={setLink}
      />
    </>
  );
};
